import { useState, useMemo } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { useUser } from '@clerk/clerk-react';
import { AppRoute } from '@agentselly/immodossier-shared';
import type {
  OverviewLoaderData,
  OverviewAttentionItem,
} from '@agentselly/immodossier-shared';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { useAppParams } from '~/helper/useAppParams';
import { useHubspotContactId } from '~/helper/useHubspotContactId';
import { LoadingSpinner } from '~/components/global/LoadingSpinner';
import { AppLink } from '~/components/AppLink';
import { Card, CardHeader, CardContent } from '@agentselly/ui';
import { EmptyState } from '~/components/empty/EmptyState';
import { ActionButton } from '~/components/button/ActionButton';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { PropertyListWrapper } from '~/components/property/PropertyListWrapper';
import type { PropertyListItemGeneric } from '~/components/property/PropertyListWrapper';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_$CONTACTID_INDEX,
)({
  component: OverviewRouteEntry,
});

export function OverviewRouteEntry() {
  const { hubspotContactId } = useHubspotContactId();
  const params = useAppParams(Route);

  const query = useLoaderQuery(AppRoute.$LANG_$COMPANYID_$CONTACTID_INDEX, {
    params: params,
    // @ts-expect-error enabled is handled dynamically
    enabled: !!hubspotContactId,
  });

  if (query.data?.v3Redesign) {
    return <OverviewPage />;
  }

  return <LegacyOverviewBody />;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function LegacyOverviewBody() {
  const { t } = useTranslate();
  const { hubspotContactId, isWaiting, timedOut } = useHubspotContactId();
  const params = useAppParams(Route);

  const query = useLoaderQuery(AppRoute.$LANG_$COMPANYID_$CONTACTID_INDEX, {
    params: params,
    // @ts-expect-error enabled is handled dynamically
    enabled: !!hubspotContactId,
  });

  if (isWaiting) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20">
        <LoadingSpinner />
        <p className="text-gray-600">Ihr Konto wird vorbereitet...</p>
      </div>
    );
  }

  if (timedOut) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20">
        <p className="text-gray-600">Es ist ein Fehler aufgetreten.</p>
        <button
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
          onClick={() => window.location.reload()}
        >
          Seite neu laden
        </button>
      </div>
    );
  }

  if (query.isLoading) {
    return <LoadingWidget />;
  }

  if (query.isError || !query.data) {
    return <ErrorWidget error={query.error} />;
  }

  const newDossierUrl = query.data?.newDossierUrl;
  const hasProperties = query.data?.properties?.length;
  const hasDossiers = query.data?.dossiers?.length;
  const hasCareProps = query.data?.careProperties?.length;
  const isExternalAdvisor = Boolean(query.data?.isExternalAdvisor);
  const isInternalAdvisor = Boolean(query.data?.isInternalAdvisor);

  if (
    !hasProperties &&
    !hasDossiers &&
    !hasCareProps &&
    !newDossierUrl &&
    !isExternalAdvisor &&
    !isInternalAdvisor
  ) {
    return (
      <Card>
        <EmptyState
          title={t('overview_properties_title')}
          description={t('empty_no_data_available')}
        />
      </Card>
    );
  }

  const uniqueProperties = Array.from(
    new Map(
      (query.data?.properties || []).map((p: any) => [
        p.id,
        p,
      ]),
    ).values(),
  );

  const uniqueDossiers = Array.from(
    new Map(
      (query.data?.dossiers || []).map((d: any) => [
        d.id,
        d,
      ]),
    ).values(),
  );

  return (
    <div className="flex flex-col gap-4">
      {isExternalAdvisor || isInternalAdvisor ? (
        <Card>
          <CardHeader>
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-lg font-bold">
                Ihre Objekte aktuell in Betreuung
              </h2>
              {isExternalAdvisor && (
                <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:whitespace-nowrap">
                  <span className="text-sm font-semibold">Lead abgeben</span>
                  <a
                    href="https://www.agentselly.ch/valiant/leadgeber/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ActionButton size="sm">Zum Formular</ActionButton>
                  </a>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              const careProperties = query.data?.careProperties || [];
              const carePropertiesUnavailable = Boolean(
                query.data?.carePropertiesUnavailable,
              );

              if (carePropertiesUnavailable) {
                return (
                  <EmptyState
                    title={'Ihre Objekte aktuell in Betreuung'}
                    description={'Daten derzeit nicht verfügbar.'}
                  />
                );
              }

              return careProperties.length ? (
                <PropertyListWrapper
                  properties={careProperties as PropertyListItemGeneric[]}
                  params={query.data.params as any}
                />
              ) : (
                <EmptyState
                  title={'Ihre Objekte aktuell in Betreuung'}
                  description={'Momentan keine aktiven Betreuungsobjekte.'}
                />
              );
            })()}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <h2 className="text-lg font-bold">
            {t('overview_properties_title')}
          </h2>
        </CardHeader>
        <CardContent>
          {(() => {
            return uniqueProperties.length ? (
              <PropertyListWrapper
                properties={uniqueProperties as PropertyListItemGeneric[]}
                params={query.data.params as any}
              />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {newDossierUrl && !uniqueDossiers.length ? (
                  <a
                    href={newDossierUrl}
                    className="group relative flex flex-col items-start gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 dark:border-gray-700 p-4 shadow-sm dark:shadow-gray-900/50 transition-all hover:border-primary-400 hover:shadow-md dark:hover:shadow-gray-900/50 active:scale-[0.98]"
                    target={
                      newDossierUrl.startsWith('/') ? undefined : '_blank'
                    }
                    rel={
                      newDossierUrl.startsWith('/') ? undefined : 'noreferrer'
                    }
                  >
                    <div className="font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary-600 transition-colors">
                      {t('overview_button_add_dossier')}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('overview_no_dossiers')}
                    </p>
                  </a>
                ) : null}
                <Link<any>
                  to={AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_COCKPIT_HOME}
                  params={{ ...params } as any}
                  className="text-sm text-gray-500 underline hover:text-primary-600 dark:text-gray-400 sm:col-span-2"
                >
                  {t(
                    'overview_button_open_demo_cockpit_link',
                    'Demo-Cockpit ansehen',
                  )}
                </Link>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-lg font-bold">
              {t('overview_dossiers_title')}
            </h2>
            {newDossierUrl && uniqueDossiers.length ? (
              <a
                href={newDossierUrl}
                target={newDossierUrl.startsWith('/') ? undefined : '_blank'}
                rel={newDossierUrl.startsWith('/') ? undefined : 'noreferrer'}
                aria-label={t('overview_button_add_dossier')}
                title={t('overview_button_add_dossier')}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-xl leading-none text-gray-700 hover:border-primary-400 hover:text-primary-600 dark:border-gray-700 dark:text-gray-200"
              >
                +
              </a>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          {uniqueDossiers.length ? (
            <LegacyDossiersList
              items={uniqueDossiers as any}
              params={query.data.params as any}
            />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <Link<any>
                to={AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_DOSSIER_HOME}
                params={{ ...params } as any}
                className="group relative flex flex-col items-start gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 dark:border-gray-700 p-4 shadow-sm dark:shadow-gray-900/50 transition-all hover:border-primary-400 hover:shadow-md dark:hover:shadow-gray-900/50 active:scale-[0.98]"
              >
                <div className="flex w-full items-center justify-between">
                  <div className="font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary-600 transition-colors">
                    {t('overview_button_open_demo_dossier')}
                  </div>
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold tracking-wide text-green-700 ring-1 ring-inset ring-green-700/20">
                    Demo
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('overview_no_dossiers_text')}
                </p>
              </Link>
              {newDossierUrl ? (
                <a
                  href={newDossierUrl}
                  className="group relative flex flex-col items-start gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 dark:border-gray-700 p-4 shadow-sm dark:shadow-gray-900/50 transition-all hover:border-primary-400 hover:shadow-md dark:hover:shadow-gray-900/50 active:scale-[0.98]"
                  target={newDossierUrl.startsWith('/') ? undefined : '_blank'}
                  rel={newDossierUrl.startsWith('/') ? undefined : 'noreferrer'}
                >
                  <div className="font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary-600 transition-colors">
                    {t('overview_button_add_dossier')}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('overview_no_dossiers')}
                  </p>
                </a>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function LegacyDossiersList({
  items,
  params,
}: {
  items: Array<{ id: string; title: string }>;
  params: any;
}) {
  const pageSize = 6;
  const pages = Math.max(1, Math.ceil((items?.length || 0) / pageSize));
  const [
    page,
    setPage,
  ] = useState(1);
  const pageItems = useMemo(
    () => (items || []).slice((page - 1) * pageSize, page * pageSize),
    [
      items,
      page,
    ],
  );

  return (
    <div className="mt-2">
      <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {pageItems.map((d) => (
          <li key={d.id}>
            <AppLink
              to={AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_HOME}
              params={{ ...params, dossierId: d.id } as any}
              className="block rounded-xl border border-gray-100 dark:border-gray-700 hover:bg-primary-50 dark:hover:bg-gray-800 p-4 transition-colors"
            >
              <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                {d.title}
              </div>
            </AppLink>
          </li>
        ))}
      </ul>
      {pages > 1 ? (
        <div className="flex justify-end items-center gap-2 mt-4">
          <button
            className="px-2 py-1 text-sm rounded border hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Zurück
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Seite {page} / {pages}
          </span>
          <button
            className="px-2 py-1 text-sm rounded border hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50"
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page === pages}
          >
            Weiter
          </button>
        </div>
      ) : null}
    </div>
  );
}

/* ─── Completed status matching ────────────────────────────────────────────── */
const DONE_STATUSES = new Set([
  'sold',
  'archived',
  'completed',
  'abgeschlossen',
  'verkauft',
  'storniert',
  'lost',
  'won',
]);

function isDoneStatus(status?: string): boolean {
  if (!status) return false;
  return DONE_STATUSES.has(status.toLowerCase().trim());
}

/* ─── Address normalization & matching ─────────────────────────────────────── */
function normalizeAddress(
  addr?:
    | string
    | { street?: string; no?: string; zip?: string; city?: string }
    | null,
): string {
  if (!addr) return '';
  if (typeof addr === 'string') {
    return addr.toLowerCase().replace(/[^a-z0-9]/g, '');
  }
  const parts = [
    addr.street,
    addr.no,
    addr.zip,
    addr.city,
  ]
    .filter(Boolean)
    .join('');
  return parts.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/* ─── Formatting helpers ───────────────────────────────────────────────────── */
function formatChf(val?: number | null): string {
  if (val === undefined || val === null || isNaN(val)) return '–';
  return (
    'CHF ' +
    Math.round(val)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, '’')
  );
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '–';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

interface StatusLocalizationEntry {
  tolgeeKey: string;
  className: string;
  labels: {
    de: string;
    fr: string;
    it: string;
    en: string;
  };
}

const STATUS_LOCALIZATIONS: Record<string, StatusLocalizationEntry> = {
  online: {
    tolgeeKey: 'cockpit_expose_widget_status_online',
    className: 'c-ok',
    labels: { de: 'Online', fr: 'En ligne', it: 'Online', en: 'Online' },
  },
  reserved: {
    tolgeeKey: 'cockpit_expose_widget_status_reserved',
    className: 'c-warn',
    labels: {
      de: 'Reserviert',
      fr: 'Réservé',
      it: 'Riservato',
      en: 'Reserved',
    },
  },
  reserviert: {
    tolgeeKey: 'cockpit_expose_widget_status_reserved',
    className: 'c-warn',
    labels: {
      de: 'Reserviert',
      fr: 'Réservé',
      it: 'Riservato',
      en: 'Reserved',
    },
  },
  active: {
    tolgeeKey: 'cockpit_expose_widget_status_online',
    className: 'c-ok',
    labels: { de: 'Aktiv', fr: 'Actif', it: 'Attivo', en: 'Active' },
  },
  aktiv: {
    tolgeeKey: 'cockpit_expose_widget_status_online',
    className: 'c-ok',
    labels: { de: 'Aktiv', fr: 'Actif', it: 'Attivo', en: 'Active' },
  },
  published: {
    tolgeeKey: 'cockpit_expose_widget_status_published',
    className: 'c-ok',
    labels: {
      de: 'Veröffentlicht',
      fr: 'Publié',
      it: 'Pubblicato',
      en: 'Published',
    },
  },
  veröffentlicht: {
    tolgeeKey: 'cockpit_expose_widget_status_published',
    className: 'c-ok',
    labels: {
      de: 'Veröffentlicht',
      fr: 'Publié',
      it: 'Pubblicato',
      en: 'Published',
    },
  },
  ready: {
    tolgeeKey: 'overview_status_ready',
    className: 'c-ok',
    labels: { de: 'Bereit', fr: 'Prêt', it: 'Pronto', en: 'Ready' },
  },
  in_bearbeitung: {
    tolgeeKey: 'overview_status_in_progress',
    className: 'c-warn',
    labels: {
      de: 'In Bearbeitung',
      fr: 'En cours',
      it: 'In elaborazione',
      en: 'In progress',
    },
  },
  'in-bearbeitung': {
    tolgeeKey: 'overview_status_in_progress',
    className: 'c-warn',
    labels: {
      de: 'In Bearbeitung',
      fr: 'En cours',
      it: 'In elaborazione',
      en: 'In progress',
    },
  },
  in_progress: {
    tolgeeKey: 'overview_status_in_progress',
    className: 'c-warn',
    labels: {
      de: 'In Bearbeitung',
      fr: 'En cours',
      it: 'In elaborazione',
      en: 'In progress',
    },
  },
  draft: {
    tolgeeKey: 'cockpit_expose_widget_status_draft',
    className: 'c-warn',
    labels: { de: 'Entwurf', fr: 'Projet', it: 'Bozza', en: 'Draft' },
  },
  entwurf: {
    tolgeeKey: 'cockpit_expose_widget_status_draft',
    className: 'c-warn',
    labels: { de: 'Entwurf', fr: 'Projet', it: 'Bozza', en: 'Draft' },
  },
  in_prüfung: {
    tolgeeKey: 'overview_status_in_review',
    className: 'c-warn',
    labels: {
      de: 'In Prüfung',
      fr: 'À l’examen',
      it: 'In esame',
      en: 'In review',
    },
  },
  vorbereitung: {
    tolgeeKey: 'cockpit_expose_widget_status_presale',
    className: 'c-warn',
    labels: {
      de: 'Vorbereitung',
      fr: 'Préparation',
      it: 'Preparazione',
      en: 'Preparation',
    },
  },
  presale: {
    tolgeeKey: 'cockpit_expose_widget_status_presale',
    className: 'c-warn',
    labels: {
      de: 'Vorbereitung',
      fr: 'Préparation',
      it: 'Preparazione',
      en: 'Preparation',
    },
  },
  leads: {
    tolgeeKey: 'cockpit_expose_widget_status_sellerLead',
    className: 'c-warn',
    labels: {
      de: 'Interessenten',
      fr: 'Intéressés',
      it: 'Interessati',
      en: 'Interested',
    },
  },
  interessent: {
    tolgeeKey: 'cockpit_expose_widget_status_sellerLead',
    className: 'c-warn',
    labels: {
      de: 'Interessenten',
      fr: 'Intéressés',
      it: 'Interessati',
      en: 'Interested',
    },
  },
  pending: {
    tolgeeKey: 'cockpit_expose_widget_status_pending',
    className: 'c-warn',
    labels: {
      de: 'Ausstehend',
      fr: 'En attente',
      it: 'In attesa',
      en: 'Pending',
    },
  },
  ausstehend: {
    tolgeeKey: 'cockpit_expose_widget_status_pending',
    className: 'c-warn',
    labels: {
      de: 'Ausstehend',
      fr: 'En attente',
      it: 'In attesa',
      en: 'Pending',
    },
  },
  storniert: {
    tolgeeKey: 'overview_status_cancelled',
    className: 'c-bad',
    labels: { de: 'Storniert', fr: 'Annulé', it: 'Annullato', en: 'Cancelled' },
  },
  abgebrochen: {
    tolgeeKey: 'overview_status_cancelled',
    className: 'c-bad',
    labels: {
      de: 'Abgebrochen',
      fr: 'Interrompu',
      it: 'Interrotto',
      en: 'Cancelled',
    },
  },
  verloren: {
    tolgeeKey: 'overview_status_lost',
    className: 'c-bad',
    labels: { de: 'Verloren', fr: 'Perdu', it: 'Perso', en: 'Lost' },
  },
  lost: {
    tolgeeKey: 'overview_status_lost',
    className: 'c-bad',
    labels: { de: 'Verloren', fr: 'Perdu', it: 'Perso', en: 'Lost' },
  },
  inactive: {
    tolgeeKey: 'overview_status_inactive',
    className: 'c-bad',
    labels: { de: 'Inaktiv', fr: 'Inactif', it: 'Inattivo', en: 'Inactive' },
  },
  inaktiv: {
    tolgeeKey: 'overview_status_inactive',
    className: 'c-bad',
    labels: { de: 'Inaktiv', fr: 'Inactif', it: 'Inattivo', en: 'Inactive' },
  },
  abgeschlossen: {
    tolgeeKey: 'cockpit_expose_widget_status_successful',
    className: 'c-neu',
    labels: {
      de: 'Abgeschlossen',
      fr: 'Terminé',
      it: 'Concluso',
      en: 'Completed',
    },
  },
  verkauft: {
    tolgeeKey: 'cockpit_expose_widget_status_successful',
    className: 'c-neu',
    labels: { de: 'Verkauft', fr: 'Vendu', it: 'Venduto', en: 'Sold' },
  },
  sold: {
    tolgeeKey: 'cockpit_expose_widget_status_successful',
    className: 'c-neu',
    labels: { de: 'Verkauft', fr: 'Vendu', it: 'Venduto', en: 'Sold' },
  },
  successful: {
    tolgeeKey: 'cockpit_expose_widget_status_successful',
    className: 'c-neu',
    labels: { de: 'Verkauft', fr: 'Vendu', it: 'Venduto', en: 'Sold' },
  },
  archived: {
    tolgeeKey: 'overview_status_archived',
    className: 'c-neu',
    labels: {
      de: 'Archiviert',
      fr: 'Archivé',
      it: 'Archiviato',
      en: 'Archived',
    },
  },
  recorded: {
    tolgeeKey: 'cockpit_expose_widget_status_recorded',
    className: 'c-warn',
    labels: {
      de: 'Objektaufnahme',
      fr: "Enregistrement de l'objet",
      it: "Registrazione dell'oggetto",
      en: 'Object recording',
    },
  },
  valuation: {
    tolgeeKey: 'overview_status_valuation',
    className: 'c-neu',
    labels: {
      de: 'Bewertung',
      fr: 'Évaluation',
      it: 'Valutazione',
      en: 'Valuation',
    },
  },
  bewertung: {
    tolgeeKey: 'overview_status_valuation',
    className: 'c-neu',
    labels: {
      de: 'Bewertung',
      fr: 'Évaluation',
      it: 'Valutazione',
      en: 'Valuation',
    },
  },
};

export function formatStatus(
  status?: string,
  t?: (key: string, defaultValue?: string) => string,
  lang = 'de',
): { label: string; className: string } | null {
  const s = (status || '').toLowerCase().trim();
  if (!s || s === 'fresh') return null;
  const normLang = (
    [
      'de',
      'fr',
      'it',
      'en',
    ].includes(lang)
      ? lang
      : 'de'
  ) as 'de' | 'fr' | 'it' | 'en';
  const entry = STATUS_LOCALIZATIONS[s];

  if (entry) {
    const fallback = entry.labels[normLang] || entry.labels.de;
    if (t) {
      const translated = t(entry.tolgeeKey, fallback);
      if (translated && translated !== entry.tolgeeKey) {
        return { label: translated, className: entry.className };
      }
    }
    return { label: fallback, className: entry.className };
  }

  const defaultLabel = {
    de: 'Info',
    fr: 'Info',
    it: 'Info',
    en: 'Info',
  }[normLang];

  if (t) {
    const translated = t('overview_status_info', defaultLabel);
    if (translated && translated !== 'overview_status_info') {
      return { label: translated, className: 'c-info' };
    }
  }
  return { label: defaultLabel, className: 'c-info' };
}

/* ─── Merged Property Structure ────────────────────────────────────────────── */
interface EvaluationData {
  timestamp?: string;
  estimatedValue?: number;
  price?: number;
  sources?: {
    iazi?: {
      estimatedValue?: number;
      estimatedLowerValue?: number;
      estimatedUpperValue?: number;
    };
  };
  estimatedLowerValue?: number;
  estimatedUpperValue?: number;
}

interface LeadData {
  name?: string;
}

interface PropertyItem {
  id: string;
  title: string;
  status?: string;
  leadStatus?: string;
  category?: string;
  dossierId?: string;
  address?: {
    street?: string;
    no?: string;
    zip?: string;
    city?: string;
  };
  numberOfRooms?: number;
  livingArea?: number;
  buildingYear?: number;
  price?: number;
  estimatedValue?: number;
  photoUrl?: string;
  upcomingAppointmentsCount?: number;
  hasUpcomingAppointments?: boolean;
  lead?: LeadData;
  evaluation?: EvaluationData;
  createdAt?: string;
}

interface DossierItem {
  id: string;
  title: string;
  propertyId?: string;
  createdAt?: string;
}

interface MergedProperty {
  id: string;
  propertyId?: string;
  dossierId?: string;
  kind: 'property' | 'dossier';
  title: string;
  status?: string;
  category?: string;
  address: {
    street?: string;
    no?: string;
    zip?: string;
    city?: string;
  };
  numberOfRooms?: number;
  livingArea?: number;
  buildingYear?: number;
  price?: number;
  estimatedValue?: number;
  evaluation?: EvaluationData;
  photoUrl?: string;
  upcomingAppointmentsCount?: number;
  hasUpcomingAppointments?: boolean;
  lead?: LeadData;
  createdAt?: string;
  dossierCreatedAt?: string;
}

/* ─── Valuation Range Meter (Direction A / v3-shared C.meter) ──────────────── */
function RangeMeter({
  lower,
  val,
  upper,
}: {
  lower: number;
  val: number;
  upper: number;
}) {
  const { t } = useTranslate();
  const sp = upper - lower || 1;
  const pad = sp * 0.25;
  const rg = sp * 1.5;
  const pc = (x: number) =>
    Math.max(
      0,
      Math.min(100, Math.round(((x - lower + pad) / rg) * 1000) / 10),
    );

  const label = t(
    'overview_range_meter_label',
    'Schätzwert {val} innerhalb {lower} bis {upper}',
    { val: formatChf(val), lower: formatChf(lower), upper: formatChf(upper) },
  );

  return (
    <div
      className="rng"
      role="img"
      aria-label={label}
    >
      <span
        className="rng-v num"
        style={{ left: `${pc(val)}%` }}
      >
        {formatChf(val)}
      </span>
      <span className="rng-t">
        <i
          className="rng-b"
          style={{
            left: `${pc(lower)}%`,
            width: `${Math.max(0, pc(upper) - pc(lower))}%`,
          }}
        />
        <i
          className="rng-d"
          style={{ left: `${pc(val)}%` }}
        />
      </span>
      <span
        className="rng-l num"
        style={{ left: `${pc(lower)}%` }}
      >
        {formatChf(lower)}
      </span>
      <span
        className="rng-l num"
        style={{ left: `${pc(upper)}%` }}
      >
        {formatChf(upper)}
      </span>
    </div>
  );
}

/* ─── SVG Icons ────────────────────────────────────────────────────────────── */
function IconDoc({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line
        x1="16"
        y1="13"
        x2="8"
        y2="13"
      />
      <line
        x1="16"
        y1="17"
        x2="8"
        y2="17"
      />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

function IconCal({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect
        x="3"
        y="4"
        width="18"
        height="18"
        rx="2"
        ry="2"
      />
      <line
        x1="16"
        y1="2"
        x2="16"
        y2="6"
      />
      <line
        x1="8"
        y1="2"
        x2="8"
        y2="6"
      />
      <line
        x1="3"
        y1="10"
        x2="21"
        y2="10"
      />
    </svg>
  );
}

function IconCoin({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
      />
      <path d="M12 6v12M15 9.5a3 3 0 0 0-6 0c0 2 3 2.5 3 4.5a3 3 0 0 1-6 0" />
    </svg>
  );
}

function IconArrowRight({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line
        x1="5"
        y1="12"
        x2="19"
        y2="12"
      />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function IconSearch({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle
        cx="11"
        cy="11"
        r="8"
      />
      <line
        x1="21"
        y1="21"
        x2="16.65"
        y2="16.65"
      />
    </svg>
  );
}

function IconGauge({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 14v-4" />
      <path d="M3.34 17a10 10 0 1 1 17.32 0" />
    </svg>
  );
}

function IconPlus({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line
        x1="12"
        y1="5"
        x2="12"
        y2="19"
      />
      <line
        x1="5"
        y1="12"
        x2="19"
        y2="12"
      />
    </svg>
  );
}

function IconInfo({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
      />
      <line
        x1="12"
        y1="16"
        x2="12"
        y2="12"
      />
      <line
        x1="12"
        y1="8"
        x2="12.01"
        y2="8"
      />
    </svg>
  );
}

function IconChevronDown({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/* ─── Main Overview Component ──────────────────────────────────────────────── */
function OverviewPage() {
  const { t } = useTranslate();
  const { user } = useUser();
  const { hubspotContactId, isWaiting, timedOut } = useHubspotContactId();
  const params = useAppParams(Route);
  const [
    searchQuery,
    setSearchQuery,
  ] = useState('');

  const query = useLoaderQuery(AppRoute.$LANG_$COMPANYID_$CONTACTID_INDEX, {
    params: params,
    // @ts-expect-error enabled is handled dynamically
    enabled: !!hubspotContactId,
  });

  /* ─── User Greeting ────────────────────────────────────────────────────────── */
  const firstName = user?.firstName || '';
  const greeting = firstName
    ? t('overview_greeting_with_name', `Guten Tag, ${firstName}`)
    : t('overview_greeting', 'Guten Tag');

  /* ─── Merge Cockpit Properties and Dossiers ────────────────────────────────── */
  const {
    mergedItems,
    activeProperties,
    doneProperties,
    totalUpcomingAppointments,
  } = useMemo(() => {
    if (!query.data) {
      return {
        mergedItems: [],
        activeProperties: [],
        doneProperties: [],
        totalUpcomingAppointments: 0,
      };
    }

    const rawProps = (query.data.properties || []) as unknown as PropertyItem[];
    const careProps = (query.data.careProperties ||
      []) as unknown as PropertyItem[];
    const rawDossiers = (query.data.dossiers || []) as unknown as DossierItem[];

    // Combine properties (favoring regular properties over careProperties on id collision)
    const propertyMap = new Map<string, PropertyItem>();
    for (const p of rawProps) {
      if (p.id) propertyMap.set(p.id, p);
    }
    for (const cp of careProps) {
      if (cp.id && !propertyMap.has(cp.id)) {
        propertyMap.set(cp.id, cp);
      }
    }
    const allProperties = Array.from(propertyMap.values());

    const matchedDossierIds = new Set<string>();
    const mergedList: MergedProperty[] = [];

    // Map properties to merged items
    for (const prop of allProperties) {
      const normProp = normalizeAddress(prop.address || prop.title);

      // Find matching dossier by cross-link ID or normalized address match
      const matchedDossier = rawDossiers.find((d) => {
        if (prop.dossierId && d.id === prop.dossierId) return true;
        if (d.propertyId && d.propertyId === prop.id) return true;
        const normDossier = normalizeAddress(d.title);
        return normProp && normDossier && normProp === normDossier;
      });

      if (matchedDossier) {
        matchedDossierIds.add(matchedDossier.id);
      }

      const evalData = prop.evaluation;
      const iazi = evalData?.sources?.iazi;
      const estimatedValue =
        iazi?.estimatedValue ??
        evalData?.estimatedValue ??
        evalData?.price ??
        prop.estimatedValue;

      mergedList.push({
        id: prop.id,
        propertyId: prop.id,
        dossierId: matchedDossier?.id || prop.dossierId,
        kind: 'property',
        title: prop.title,
        status: prop.status || prop.leadStatus,
        category: prop.category,
        address: {
          street: prop.address?.street,
          no: prop.address?.no,
          zip: prop.address?.zip,
          city: prop.address?.city,
        },
        numberOfRooms: prop.numberOfRooms,
        livingArea: prop.livingArea,
        buildingYear: prop.buildingYear,
        price: prop.price,
        estimatedValue: estimatedValue,
        evaluation: evalData,
        photoUrl: prop.photoUrl,
        upcomingAppointmentsCount: prop.upcomingAppointmentsCount || 0,
        hasUpcomingAppointments: Boolean(
          prop.hasUpcomingAppointments ||
            (prop.upcomingAppointmentsCount &&
              prop.upcomingAppointmentsCount > 0),
        ),
        lead: prop.lead,
        createdAt: prop.createdAt,
        dossierCreatedAt: matchedDossier?.createdAt,
      });
    }

    // Add remaining unmatched dossiers
    for (const dos of rawDossiers) {
      if (!matchedDossierIds.has(dos.id)) {
        mergedList.push({
          id: dos.id,
          dossierId: dos.id,
          kind: 'dossier',
          title: dos.title,
          address: {
            street: dos.title,
          },
          createdAt: dos.createdAt,
          dossierCreatedAt: dos.createdAt,
        });
      }
    }

    const active = mergedList.filter((m) => !isDoneStatus(m.status));
    const done = mergedList.filter((m) => isDoneStatus(m.status));
    const totalAppts = active.reduce(
      (sum, p) => sum + (p.upcomingAppointmentsCount || 0),
      0,
    );

    return {
      mergedItems: mergedList,
      activeProperties: active,
      doneProperties: done,
      totalUpcomingAppointments: totalAppts,
    };
  }, [query.data]);

  /* ─── Filtered Portfolio for Advisor View ──────────────────────────────────── */
  const filteredActivePortfolio = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return activeProperties;
    return activeProperties.filter((item) => {
      const addrStr = [
        item.address.street,
        item.address.no,
        item.address.zip,
        item.address.city,
        item.title,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const leadStr = (item.lead?.name || '').toLowerCase();
      const catStr = (item.category || '').toLowerCase();
      return addrStr.includes(q) || leadStr.includes(q) || catStr.includes(q);
    });
  }, [
    activeProperties,
    searchQuery,
  ]);

  /* ─── Loading / Waiting States ─────────────────────────────────────────────── */
  if (isWaiting) {
    return (
      <div className="v3-overview flex flex-col items-center justify-center gap-4 py-20 min-h-[360px]">
        <LoadingSpinner />
        <p className="mu sm">
          {t('overview_account_preparing', 'Ihr Konto wird vorbereitet...')}
        </p>
      </div>
    );
  }

  if (timedOut) {
    return (
      <div className="v3-overview flex flex-col items-center justify-center gap-4 py-20 min-h-[360px]">
        <p className="mu sm font-semibold">
          {t('overview_error_occurred', 'Es ist ein Fehler aufgetreten.')}
        </p>
        <button
          className="btn pri"
          onClick={() => window.location.reload()}
        >
          {t('overview_button_reload_page', 'Seite neu laden')}
        </button>
      </div>
    );
  }

  if (query.isLoading) {
    return (
      <OverviewSkeleton isAdvisor={Boolean(query.data?.isInternalAdvisor)} />
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="v3-overview">
        <div
          className="card stack state-err"
          data-od-id="overview-error"
        >
          <div className="sec">
            <h2>
              {t(
                'overview_error_title',
                'Übersicht konnte nicht geladen werden',
              )}
            </h2>
          </div>
          <p className="mu sm">
            {t(
              'overview_error_desc',
              'Die Daten konnten nicht geladen werden. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
            )}
          </p>
          <div>
            <button
              className="btn pri"
              onClick={() => query.refetch()}
              data-od-id="btn-retry"
            >
              {t('overview_button_retry', 'Erneut versuchen')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const {
    isExternalAdvisor,
    isInternalAdvisor,
    newDossierUrl,
    overviewAggregate,
  } = query.data;

  /* ─── Role 1: External Advisor (e.g. Nesseler 623799725254) ────────────────── */
  if (isExternalAdvisor) {
    return (
      <div className="v3-overview stack">
        <div
          className="head"
          data-od-id="greeting"
        >
          <h1>{greeting}</h1>
          <p className="mu">
            {t(
              'overview_external_advisor_title',
              'Ihre Objekte aktuell in Betreuung',
            )}
          </p>
        </div>

        {activeProperties.length === 0 ? (
          <div
            className="card stack state-empty"
            data-od-id="external-advisor-empty"
          >
            <div className="sec">
              <h2>
                {t(
                  'overview_external_advisor_title',
                  'Ihre Objekte aktuell in Betreuung',
                )}
              </h2>
            </div>
            <p className="mu sm">
              {t(
                'overview_external_advisor_empty_desc',
                'Momentan keine aktiven Betreuungsobjekte.',
              )}
            </p>
            <div className="row wrap pt-2">
              <a
                href="https://www.agentselly.ch/valiant/leadgeber/"
                target="_blank"
                rel="noreferrer"
                className="btn pri"
                data-od-id="cta-submit-lead"
              >
                {t('overview_external_advisor_submit_lead', 'Lead abgeben')}
                <IconArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        ) : (
          <div className="stack">
            <div className="card">
              <div className="sec">
                <h2>
                  {t(
                    'overview_external_advisor_title',
                    'Ihre Objekte aktuell in Betreuung',
                  )}
                </h2>
                <a
                  href="https://www.agentselly.ch/valiant/leadgeber/"
                  target="_blank"
                  rel="noreferrer"
                  className="btn"
                  data-od-id="cta-submit-lead-header"
                >
                  {t('overview_external_advisor_submit_lead', 'Lead abgeben')}
                  <IconArrowRight className="w-4 h-4" />
                </a>
              </div>
              <div id="plist">
                {activeProperties.map((prop) => (
                  <PropertyRowItem
                    key={prop.id}
                    property={prop}
                    params={params}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        <DataNoteFooter />
      </div>
    );
  }

  /* ─── Role 2: Internal Advisor ─────────────────────────────────────────────── */
  if (isInternalAdvisor) {
    return (
      <InternalAdvisorView
        greeting={greeting}
        activeProperties={activeProperties}
        doneProperties={doneProperties}
        filteredPortfolio={filteredActivePortfolio}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        totalUpcomingAppointments={totalUpcomingAppointments}
        overviewAggregate={overviewAggregate}
        newDossierUrl={newDossierUrl}
        params={params}
      />
    );
  }

  /* ─── Role 3: Single-Property Owner (Exactly 1 Object) ─────────────────────── */
  if (mergedItems.length === 1) {
    return (
      <SinglePropertyOwnerView
        greeting={greeting}
        property={mergedItems[0]}
        params={params}
      />
    );
  }

  /* ─── Role 4: Multi-Property Owner (or 0 Properties) ───────────────────────── */
  return (
    <MultiPropertyOwnerView
      greeting={greeting}
      items={mergedItems}
      newDossierUrl={newDossierUrl}
      params={params}
    />
  );
}

/* ─── Internal Advisor View ────────────────────────────────────────────────── */
function InternalAdvisorView({
  greeting,
  activeProperties,
  doneProperties,
  filteredPortfolio,
  searchQuery,
  setSearchQuery,
  totalUpcomingAppointments,
  overviewAggregate,
  newDossierUrl,
  params,
}: {
  greeting: string;
  activeProperties: MergedProperty[];
  doneProperties: MergedProperty[];
  filteredPortfolio: MergedProperty[];
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  totalUpcomingAppointments: number;
  overviewAggregate?: OverviewLoaderData['overviewAggregate'];
  newDossierUrl?: string;
  params: Record<string, string | undefined>;
}) {
  const { t } = useTranslate();

  const activeCount =
    overviewAggregate?.activePropertiesCount ?? activeProperties.length;
  const inqCount = overviewAggregate?.interestedTotal ?? 0;
  const offCount = overviewAggregate?.offersTotal ?? 0;

  // Attention items
  const attentionItems: OverviewAttentionItem[] =
    overviewAggregate?.attentionItems || [];

  // Properties with upcoming appointments
  const propertiesWithAppointments = activeProperties.filter(
    (p) => (p.upcomingAppointmentsCount || 0) > 0,
  );

  return (
    <div className="v3-overview stack">
      {/* Header */}
      <div
        className="head"
        data-od-id="greeting"
      >
        <h1>{greeting}</h1>
        <p className="mu">
          ImmoCockpit
          {activeCount > 0
            ? ` · ${activeCount} ${t(
                'overview_subtitle_active_objects',
                'aktive Objekte',
              )}`
            : ''}
        </p>
      </div>

      {activeCount === 0 &&
      mergedItemsCount(activeProperties, doneProperties) === 0 ? (
        /* Empty State */
        <div className="stack">
          <div
            className="grid g4"
            data-od-id="kpi-row"
          >
            <KpiCard
              value="–"
              label={t('overview_kpi_objects_in_care', 'Objekte in Betreuung')}
            />
            <KpiCard
              value="–"
              label={t('overview_kpi_appointments_7_days', 'Termine (7 Tage)')}
            />
            <KpiCard
              value="–"
              label={t('overview_kpi_new_inquiries', 'Neue Anfragen')}
            />
            <KpiCard
              value="–"
              label={t('overview_kpi_open_offers', 'Offene Angebote')}
            />
          </div>
          <div className="card stack state-empty">
            <div className="sec">
              <h2>
                {t(
                  'overview_empty_advisor_title',
                  'Noch keine Objekte in Betreuung',
                )}
              </h2>
            </div>
            <p className="mu sm">
              {t(
                'overview_empty_advisor_desc',
                'Sobald Ihnen ein Objekt zugewiesen ist, erscheinen hier Termine, Anfragen und der Status jedes Objekts. Bis dahin können Sie das ImmoCockpit mit Demodaten ausprobieren oder ein neues Dossier anlegen.',
              )}
            </p>
            <div className="row wrap pt-2">
              {newDossierUrl ? (
                <a
                  href={newDossierUrl}
                  target={newDossierUrl.startsWith('/') ? undefined : '_blank'}
                  rel={newDossierUrl.startsWith('/') ? undefined : 'noreferrer'}
                  className="btn pri"
                  data-od-id="cta-new-dossier"
                >
                  <IconPlus className="w-4 h-4" />
                  {t('overview_button_add_dossier', 'Neues Dossier')}
                </a>
              ) : null}
              <Link
                to={AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_COCKPIT_HOME}
                params={params}
                className={newDossierUrl ? 'btn ghost' : 'btn pri'}
                data-od-id="cta-demo-cockpit"
              >
                {t(
                  'overview_button_open_demo_cockpit',
                  'Demo-ImmoCockpit öffnen',
                )}
              </Link>
            </div>
          </div>
        </div>
      ) : (
        /* Active State */
        <div className="stack">
          {/* Top KPI Row */}
          <div
            className="grid g4"
            data-od-id="kpi-row"
          >
            <KpiCard
              id="kpi-objects"
              value={activeCount}
              label={t('overview_kpi_objects_in_care', 'Objekte in Betreuung')}
            />
            <KpiCard
              id="kpi-appointments"
              value={totalUpcomingAppointments}
              label={t('overview_kpi_appointments_7_days', 'Termine (7 Tage)')}
            />
            <KpiCard
              id="kpi-inquiries"
              value={inqCount}
              label={t('overview_kpi_new_inquiries', 'Neue Anfragen')}
              hasNewBadge={inqCount > 0}
            />
            <KpiCard
              id="kpi-offers"
              value={offCount}
              label={t('overview_kpi_open_offers', 'Offene Angebote')}
              hasNewBadge={offCount > 0}
            />
          </div>

          {/* Split Grid: Attention & Appointments */}
          <div className="grid split g2">
            {/* Attention Section */}
            <section
              className="card"
              data-od-id="needs-attention"
              aria-labelledby="h-att"
            >
              <div className="sec">
                <h2 id="h-att">
                  {t(
                    'overview_needs_attention_title',
                    'Braucht Aufmerksamkeit',
                  )}
                  <span className="new">NEU</span>
                </h2>
                <span className="xs mu">
                  {attentionItems.length}{' '}
                  {t('overview_attention_open_label', 'offen')}
                </span>
              </div>
              {attentionItems.length ? (
                attentionItems.map((item) => {
                  const isDocument = item.type === 'missing_document';
                  const targetRoute = isDocument
                    ? AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_DOCUMENTS
                    : AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_OFFERS;

                  return (
                    <Link
                      key={item.id}
                      to={targetRoute}
                      params={{ ...params, propertyId: item.propertyId }}
                      className="item att"
                      data-od-id={`attention-${item.propertyId}`}
                    >
                      <span className="ico">
                        {isDocument ? (
                          <IconDoc className="w-5 h-5" />
                        ) : (
                          <IconCoin className="w-5 h-5" />
                        )}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="t block">
                          {item.propertyTitle || item.title}
                        </span>
                        <span className="sm mu">
                          {item.description || item.title}
                        </span>
                      </span>
                      <span className="mu flex-none self-center">
                        <IconArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                  );
                })
              ) : (
                <p className="sm mu py-2">
                  {t('overview_no_attention_items', 'Keine offenen Punkte.')}
                </p>
              )}
            </section>

            {/* Next Appointments Section */}
            <section
              className="card"
              data-od-id="next-appointments"
              aria-labelledby="h-next"
            >
              <div className="sec">
                <h2 id="h-next">
                  {t('overview_next_appointments_title', 'Nächste Termine')}
                </h2>
                <span className="xs mu">
                  {totalUpcomingAppointments}{' '}
                  {t('overview_appointments_in_7_days', 'in 7 Tagen')}
                </span>
              </div>
              {propertiesWithAppointments.length ? (
                propertiesWithAppointments.map((prop) => (
                  <Link
                    key={prop.id}
                    to={
                      AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_CALENDAR
                    }
                    params={{ ...params, propertyId: prop.id }}
                    className="item"
                    data-od-id={`appt-${prop.id}`}
                  >
                    <span className="ico">
                      <IconCal className="w-5 h-5" />
                    </span>
                    <span className="min-w-0">
                      <span className="t block">
                        {prop.upcomingAppointmentsCount}{' '}
                        {prop.upcomingAppointmentsCount === 1
                          ? t('overview_appointment_singular', 'Termin')
                          : t('overview_appointment_plural', 'Termine')}{' '}
                        · {displayAddress(prop.address, prop.title)}
                      </span>
                      <span className="sm mu">{prop.address.city || ''}</span>
                    </span>
                  </Link>
                ))
              ) : (
                <p className="sm mu py-2">
                  {t(
                    'overview_no_appointments',
                    'Keine anstehenden Termine in den nächsten 7 Tagen.',
                  )}
                </p>
              )}
              <p className="xs mu mt-2">
                {t(
                  'overview_appointments_note',
                  'Termine werden dynamisch aus dem Kalender geladen.',
                )}
              </p>
            </section>
          </div>

          {/* Searchable Portfolio */}
          <section
            className="card"
            data-od-id="portfolio"
            aria-labelledby="h-port"
          >
            <div className="sec">
              <h2 id="h-port">{t('overview_portfolio_title', 'Portfolio')}</h2>
              <label className="search">
                <IconSearch className="w-4 h-4" />
                <span className="sr">
                  {t('overview_search_sr', 'Objekte durchsuchen')}
                </span>
                <input
                  id="q"
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t(
                    'overview_search_placeholder',
                    'Adresse oder Eigentümer',
                  )}
                  autoComplete="off"
                />
              </label>
            </div>

            <div id="plist">
              {filteredPortfolio.map((prop) => (
                <PropertyRowItem
                  key={prop.id}
                  property={prop}
                  params={params}
                />
              ))}
            </div>

            {filteredPortfolio.length === 0 && (
              <p
                id="nohit"
                className="sm mu py-3"
                data-od-id="search-nohit"
              >
                {t(
                  'overview_search_no_results',
                  'Kein Objekt passt zur Suche.',
                )}
              </p>
            )}
          </section>

          {/* Completed Accordion */}
          {doneProperties.length > 0 && (
            <details
              className="acc"
              data-od-id="accordion-done"
            >
              <summary>
                <span>
                  {t(
                    'overview_completed_objects_title',
                    'Abgeschlossene Objekte',
                  )}{' '}
                  ({doneProperties.length})
                </span>
                <IconChevronDown className="chev w-5 h-5 transition-transform" />
              </summary>
              <div className="in">
                {doneProperties.map((prop) => (
                  <CompletedPropertyRowItem
                    key={prop.id}
                    property={prop}
                    params={params}
                  />
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      <DataNoteFooter />
    </div>
  );
}

/* ─── Single Property Owner View (Hero Layout) ─────────────────────────────── */
function SinglePropertyOwnerView({
  greeting,
  property,
  params,
}: {
  greeting: string;
  property: MergedProperty;
  params: Record<string, string | undefined>;
}) {
  const { t } = useTranslate();
  const chipInfo = formatStatus(property.status, t, params.lang);

  // Valuation range data
  const ev = property.evaluation;
  const iazi = ev?.sources?.iazi;
  const estVal =
    iazi?.estimatedValue ??
    ev?.estimatedValue ??
    ev?.price ??
    property.estimatedValue;
  const lowerVal =
    iazi?.estimatedLowerValue ??
    ev?.estimatedLowerValue ??
    (estVal ? Math.round(estVal * 0.9) : undefined);
  const upperVal =
    iazi?.estimatedUpperValue ??
    ev?.estimatedUpperValue ??
    (estVal ? Math.round(estVal * 1.1) : undefined);

  const hasEvaluation = estVal && lowerVal && upperVal && lowerVal < upperVal;

  return (
    <div className="v3-overview stack">
      <div
        className="head"
        data-od-id="greeting"
      >
        <h1>{greeting}</h1>
        <p className="mu">
          {t('overview_single_object_title', 'Ihr Objekt im Überblick')}
        </p>
      </div>

      <article
        className="card one"
        data-od-id="single-object"
      >
        {/* Media Column */}
        <div className="obj">
          <div className="media">
            {property.photoUrl ? (
              <img
                src={property.photoUrl}
                alt={property.title}
                className="pic"
                loading="eager"
              />
            ) : (
              <div className="pic-ph">
                <span>{property.address.city || 'Immobilie'}</span>
              </div>
            )}
            {chipInfo && (
              <span className={`chip ${chipInfo.className}`}>
                <i />
                {chipInfo.label}
              </span>
            )}
          </div>
        </div>

        {/* Details Stack Column */}
        <div
          className="stack"
          style={{ gap: '20px' }}
        >
          <div>
            {property.category ? (
              <p className="eyebrow">{property.category}</p>
            ) : null}
            <h2 className="text-2xl font-bold leading-tight">
              {displayAddress(property.address, property.title)}
            </h2>
            <p className="mu">{property.address.city || ''}</p>
          </div>

          <dl className="dl d3">
            <div>
              <dt>{t('overview_rooms', 'Zimmer')}</dt>
              <dd>{property.numberOfRooms ?? '–'}</dd>
            </div>
            <div>
              <dt>{t('overview_living_area', 'Wohnfläche')}</dt>
              <dd>{property.livingArea ? `${property.livingArea} m²` : '–'}</dd>
            </div>
            <div>
              <dt>{t('overview_building_year', 'Baujahr')}</dt>
              <dd>{property.buildingYear ?? '–'}</dd>
            </div>
            <div>
              <dt>{t('overview_sale_price', 'Verkaufspreis')}</dt>
              <dd>{formatChf(property.price)}</dd>
            </div>
            <div>
              <dt>{t('overview_estimated_value', 'Geschätzter Wert')}</dt>
              <dd>{formatChf(estVal)}</dd>
            </div>
            <div>
              <dt>{t('overview_dossier_since', 'Dossier seit')}</dt>
              <dd>
                {formatDate(property.dossierCreatedAt || property.createdAt)}
              </dd>
            </div>
          </dl>

          {/* Next Appointment Shortcut */}
          <Link
            to={
              AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_CALENDAR
            }
            params={{
              ...params,
              propertyId: property.propertyId || property.id,
            }}
            className="item border border-zinc-200 dark:border-zinc-800 rounded-lg p-3"
            data-od-id="single-next-appointment"
          >
            <span className="ico">
              <IconCal className="w-5 h-5" />
            </span>
            <span className="min-w-0">
              <span className="t block">
                {property.upcomingAppointmentsCount || 0}{' '}
                {t(
                  'overview_upcoming_appointments_label',
                  'bevorstehende Termine',
                )}
              </span>
              <span className="sm mu">
                {property.address.city ? `${property.address.city} · ` : ''}
                {t('overview_calendar_action_hint', 'Im Kalender ansehen')}
              </span>
            </span>
          </Link>

          {/* Action Buttons */}
          <div className="row wrap pt-1">
            <Link
              to={AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_HOME}
              params={{
                ...params,
                propertyId: property.propertyId || property.id,
              }}
              className="btn pri"
              data-od-id="cta-open-cockpit"
            >
              {t('overview_button_open_cockpit', 'ImmoCockpit öffnen')}
              <IconArrowRight className="w-4 h-4" />
            </Link>

            {property.dossierId ? (
              <Link
                to={
                  AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_HOME
                }
                params={{ ...params, dossierId: property.dossierId }}
                className="btn"
                data-od-id="cta-open-dossier"
              >
                {t('overview_button_open_dossier', 'Dossier ansehen')}
              </Link>
            ) : null}
          </div>
        </div>
      </article>

      {/* Valuation Range Card */}
      {hasEvaluation && (
        <section
          className="card"
          data-od-id="single-evaluation"
          aria-labelledby="h-ev"
        >
          <div className="sec">
            <h2 id="h-ev">{t('overview_evaluation_title', 'Bewertung')}</h2>
            <span className="xs mu">
              {ev?.timestamp
                ? `${t('overview_evaluation_as_of', 'Stand')} ${formatDate(
                    ev.timestamp,
                  )}`
                : ''}
            </span>
          </div>
          <p className="num text-2xl font-semibold my-1 mb-3">
            {formatChf(estVal)}
          </p>
          <RangeMeter
            lower={lowerVal!}
            val={estVal!}
            upper={upperVal!}
          />
        </section>
      )}

      <div
        className="callout"
        data-od-id="direct-object-note"
      >
        <IconInfo className="w-5 h-5 flex-none" />
        <span>
          {t(
            'overview_direct_object_note',
            'Direkt zum Objekt statt Liste – ein Eigentümer mit nur einem Objekt sieht sein Objekt ohne Umweg über eine Liste.',
          )}
        </span>
      </div>

      <DataNoteFooter />
    </div>
  );
}

/* ─── Multi-Property Owner View ────────────────────────────────────────────── */
function MultiPropertyOwnerView({
  greeting,
  items,
  newDossierUrl,
  params,
}: {
  greeting: string;
  items: MergedProperty[];
  newDossierUrl?: string;
  params: Record<string, string | undefined>;
}) {
  const { t } = useTranslate();
  const mergedCount = items.filter(
    (o) => o.kind === 'property' && o.dossierId,
  ).length;

  if (items.length === 0) {
    return (
      <div className="v3-overview stack">
        <div
          className="head"
          data-od-id="greeting"
        >
          <h1>{greeting}</h1>
        </div>
        <div
          className="card stack state-empty"
          data-od-id="owner-empty"
        >
          <div className="sec">
            <h2>
              {t(
                'overview_empty_owner_title',
                'Noch kein Objekt und kein Dossier',
              )}
            </h2>
          </div>
          <p className="mu sm">
            {t(
              'overview_empty_owner_desc',
              'Mit einem Dossier erhalten Sie eine Bewertung Ihrer Immobilie sowie Daten zu Region und Markt. Im Demo-ImmoCockpit sehen Sie, wie der Verkauf begleitet wird.',
            )}
          </p>
          <div className="row wrap pt-2">
            {newDossierUrl ? (
              <a
                href={newDossierUrl}
                target={newDossierUrl.startsWith('/') ? undefined : '_blank'}
                rel={newDossierUrl.startsWith('/') ? undefined : 'noreferrer'}
                className="btn pri"
                data-od-id="cta-new-dossier"
              >
                <IconPlus className="w-4 h-4" />
                {t('overview_button_add_dossier', 'Dossier erstellen')}
              </a>
            ) : null}
            <Link
              to={AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_COCKPIT_HOME}
              params={params}
              className={newDossierUrl ? 'btn ghost' : 'btn pri'}
              data-od-id="cta-demo-cockpit"
            >
              {t(
                'overview_button_open_demo_cockpit',
                'Demo-ImmoCockpit ansehen',
              )}
            </Link>
          </div>
        </div>
        <DataNoteFooter />
      </div>
    );
  }

  return (
    <div className="v3-overview stack">
      <div
        className="head"
        data-od-id="greeting"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div>
          <h1>{greeting}</h1>
          <p className="mu">
            {items.length}{' '}
            {t(
              'overview_objects_merged_summary',
              'Objekte · ImmoCockpit und Dossier pro Adresse zusammengefasst',
            )}
          </p>
        </div>
        {newDossierUrl ? (
          <a
            href={newDossierUrl}
            target={newDossierUrl.startsWith('/') ? undefined : '_blank'}
            rel={newDossierUrl.startsWith('/') ? undefined : 'noreferrer'}
            className="btn pri"
            data-od-id="cta-add-object"
          >
            <IconPlus className="w-4 h-4" />
            {t('overview_button_add_object', 'Weiteres Objekt')}
          </a>
        ) : null}
      </div>

      <div
        className="grid g2"
        data-od-id="object-grid"
      >
        {items.map((item) => (
          <OwnerPropertyCard
            key={item.id}
            item={item}
            params={params}
          />
        ))}
      </div>

      {mergedCount > 0 && (
        <div
          className="callout"
          data-od-id="merge-note"
        >
          <IconInfo className="w-5 h-5 flex-none" />
          <span>
            {mergedCount}{' '}
            {t(
              'overview_merge_callout_note',
              'Objekte haben sowohl ImmoCockpit als auch Dossier. Die Zusammenführung nach Adresse fasst beide zusammen.',
            )}
          </span>
        </div>
      )}

      <DataNoteFooter />
    </div>
  );
}

/* ─── Owner Merged Property Card ───────────────────────────────────────────── */
function OwnerPropertyCard({
  item,
  params,
}: {
  item: MergedProperty;
  params: Record<string, string | undefined>;
}) {
  const { t } = useTranslate();
  const isDossierOnly = item.kind === 'dossier';
  const chipInfo = formatStatus(item.status, t, params.lang);

  return (
    <article
      className="card obj"
      data-od-id={`object-${item.id}`}
    >
      <div className="media">
        {item.photoUrl ? (
          <img
            src={item.photoUrl}
            alt={item.title}
            className="pic"
            loading="lazy"
          />
        ) : (
          <div className="pic-ph">
            <span>{item.address.city || 'Immobilie'}</span>
          </div>
        )}
        {!isDossierOnly && chipInfo && (
          <span className={`chip ${chipInfo.className}`}>
            <i />
            {chipInfo.label}
          </span>
        )}
      </div>

      <div>
        <h2 className="text-lg font-bold">
          {displayAddress(item.address, item.title)}
        </h2>
        <p className="sm mu">{item.address.city || ''}</p>
      </div>

      {isDossierOnly ? (
        <div
          className="stack"
          style={{ gap: '8px' }}
        >
          <div className="facts">
            <span>
              {t('overview_dossier_created_at', 'Dossier erstellt am')}{' '}
              {formatDate(item.dossierCreatedAt || item.createdAt)}
            </span>
          </div>
          <p className="sm mu">
            {t(
              'overview_dossier_features_desc',
              'Bewertung, Region und Marktdaten zu dieser Adresse.',
            )}
          </p>
        </div>
      ) : (
        <div
          className="stack"
          style={{ gap: '8px' }}
        >
          <div className="facts">
            {item.numberOfRooms && (
              <span>
                {item.numberOfRooms} {t('overview_rooms', 'Zimmer')}
              </span>
            )}
            {item.livingArea && <span>{item.livingArea} m²</span>}
            {item.buildingYear && <span>{item.buildingYear}</span>}
          </div>
          <dl className="dl">
            <div>
              <dt>{t('overview_estimated_value', 'Geschätzter Wert')}</dt>
              <dd>{formatChf(item.estimatedValue)}</dd>
            </div>
            <div>
              <dt>
                {t(
                  'overview_upcoming_appointments_label',
                  'Bevorstehende Termine',
                )}
              </dt>
              <dd>{item.upcomingAppointmentsCount || 0}</dd>
            </div>
          </dl>
        </div>
      )}

      <div className="acts">
        {!isDossierOnly && item.propertyId && (
          <Link
            to={AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_HOME}
            params={{ ...params, propertyId: item.propertyId }}
            className="btn"
            data-od-id={`object-${item.id}-cockpit`}
          >
            <IconGauge className="w-4 h-4" />
            {t('overview_button_cockpit', 'ImmoCockpit')}
          </Link>
        )}
        {item.dossierId && (
          <Link
            to={AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_HOME}
            params={{ ...params, dossierId: item.dossierId }}
            className="btn"
            data-od-id={`object-${item.id}-dossier`}
          >
            <IconDoc className="w-4 h-4" />
            {t('overview_button_dossier', 'Dossier')}
          </Link>
        )}
      </div>
    </article>
  );
}

/* ─── Property Row Item (Portfolio List) ───────────────────────────────────── */
function PropertyRowItem({
  property,
  params,
}: {
  property: MergedProperty;
  params: Record<string, string | undefined>;
}) {
  const { t } = useTranslate();
  const chipInfo = formatStatus(property.status, t, params.lang);

  const targetRoute =
    property.propertyId || property.kind === 'property'
      ? AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_HOME
      : AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_HOME;

  const targetParams =
    property.propertyId || property.kind === 'property'
      ? { ...params, propertyId: property.propertyId || property.id }
      : { ...params, dossierId: property.dossierId || property.id };

  return (
    <Link
      to={targetRoute}
      params={targetParams}
      className="prow"
      data-od-id={`portfolio-${property.id}`}
    >
      <span className="thumb">
        {property.photoUrl ? (
          <img
            src={property.photoUrl}
            alt={property.title}
            className="pic"
            loading="lazy"
          />
        ) : (
          <div className="pic-ph">
            <span>
              {property.address.city ||
                t('overview_property_fallback', 'Objekt')}
            </span>
          </div>
        )}
      </span>

      <span className="body">
        <span className="t block">
          {displayAddress(property.address, property.title)}
        </span>
        <span className="xs mu block">
          {[
            property.address.city,
            property.category,
            property.lead?.name,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
        <span className="facts">
          {property.numberOfRooms ? (
            <span>
              {property.numberOfRooms} {t('overview_rooms', 'Zimmer')}
            </span>
          ) : null}
          {property.livingArea ? <span>{property.livingArea} m²</span> : null}
          {property.buildingYear ? <span>{property.buildingYear}</span> : null}
        </span>

        {/* Mobile End Block (<1100px) */}
        <span className="end">
          {chipInfo && (
            <span className={`chip ${chipInfo.className}`}>
              <i />
              {chipInfo.label}
            </span>
          )}
          <span className="xs mu mval">
            {t('overview_estimated_value', 'Geschätzter Wert')}{' '}
            <b className="num text-zinc-900 dark:text-zinc-100">
              {formatChf(property.estimatedValue)}
            </b>
          </span>
          {property.upcomingAppointmentsCount ? (
            <span className="xs mu inline-flex items-center gap-1">
              <IconCal className="w-3.5 h-3.5" />
              {property.upcomingAppointmentsCount}{' '}
              {property.upcomingAppointmentsCount === 1
                ? t('overview_appointment_singular', 'Termin')
                : t('overview_appointment_plural', 'Termine')}
            </span>
          ) : null}
        </span>
      </span>

      {/* Desktop Value Column (>=1100px) */}
      <span className="val">
        <span className="xs mu block">
          {t('overview_estimated_value', 'Geschätzter Wert')}
        </span>
        <span className="num font-semibold">
          {formatChf(property.estimatedValue)}
        </span>
      </span>
    </Link>
  );
}

/* ─── Completed Property Row Item ─────────────────────────────────────────── */
function CompletedPropertyRowItem({
  property,
  params,
}: {
  property: MergedProperty;
  params: Record<string, string | undefined>;
}) {
  const { t } = useTranslate();
  const chipInfo = formatStatus(property.status, t, params.lang);

  return (
    <Link
      to={AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_HOME}
      params={{ ...params, propertyId: property.propertyId || property.id }}
      className="prow"
    >
      <span
        className="thumb"
        style={{ width: '72px' }}
      >
        {property.photoUrl ? (
          <img
            src={property.photoUrl}
            alt={property.title}
            className="pic"
            loading="lazy"
          />
        ) : (
          <div className="pic-ph">
            <span>
              {property.address.city ||
                t('overview_property_fallback', 'Objekt')}
            </span>
          </div>
        )}
      </span>
      <span className="body">
        <span className="t block">
          {displayAddress(property.address, property.title)}
        </span>
        <span className="xs mu">
          {[
            property.address.city,
            formatChf(property.price),
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
      {chipInfo && (
        <span className={`chip ${chipInfo.className}`}>
          <i />
          {chipInfo.label}
        </span>
      )}
    </Link>
  );
}

/* ─── KPI Card ─────────────────────────────────────────────────────────────── */
function KpiCard({
  id,
  value,
  label,
  hasNewBadge = false,
}: {
  id?: string;
  value: number | string;
  label: string;
  hasNewBadge?: boolean;
}) {
  return (
    <div
      className="kpi"
      data-od-id={id}
    >
      <div className="n">
        {value}
        {hasNewBadge && <span className="new">NEU</span>}
      </div>
      <div className="l">{label}</div>
    </div>
  );
}

/* ─── Skeleton Loading Component ───────────────────────────────────────────── */
function OverviewSkeleton({ isAdvisor }: { isAdvisor?: boolean }) {
  return (
    <div
      className="v3-overview stack"
      aria-busy="true"
      aria-label="Wird geladen"
    >
      <div
        className="sk"
        style={{ height: '32px', width: '40%' }}
      />

      {isAdvisor ? (
        <div className="stack">
          <div className="grid g4">
            {[
              0,
              1,
              2,
              3,
            ].map((i) => (
              <div
                key={i}
                className="card stack"
                style={{ gap: '8px' }}
              >
                <div
                  className="sk"
                  style={{ height: '36px', width: '50%' }}
                />
                <div
                  className="sk"
                  style={{ height: '16px', width: '80%' }}
                />
              </div>
            ))}
          </div>
          <div className="grid split g2">
            <div className="card stack">
              <div
                className="sk"
                style={{ height: '24px', width: '60%' }}
              />
              <div
                className="sk"
                style={{ height: '60px' }}
              />
            </div>
            <div className="card stack">
              <div
                className="sk"
                style={{ height: '24px', width: '60%' }}
              />
              <div
                className="sk"
                style={{ height: '60px' }}
              />
            </div>
          </div>
          <div className="card stack">
            <div
              className="sk"
              style={{ height: '28px', width: '30%' }}
            />
            {[
              0,
              1,
            ].map((i) => (
              <div
                key={i}
                className="sk"
                style={{ height: '80px' }}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="grid g2">
          {[
            0,
            1,
          ].map((i) => (
            <div
              key={i}
              className="card stack"
              style={{ gap: '12px' }}
            >
              <div
                className="sk"
                style={{ height: '180px' }}
              />
              <div
                className="sk"
                style={{ height: '24px', width: '70%' }}
              />
              <div
                className="sk"
                style={{ height: '16px', width: '50%' }}
              />
              <div
                className="sk"
                style={{ height: '44px' }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Helper: Display address text ─────────────────────────────────────────── */
function displayAddress(
  address: { street?: string; no?: string; zip?: string; city?: string },
  fallbackTitle: string,
): string {
  const streetNo = [
    address.street,
    address.no,
  ]
    .filter(Boolean)
    .join(' ');
  if (streetNo) return streetNo;
  return fallbackTitle || '–';
}

function mergedItemsCount(
  active: MergedProperty[],
  done: MergedProperty[],
): number {
  return active.length + done.length;
}

/* ─── Footer Data Note ─────────────────────────────────────────────────────── */
function DataNoteFooter() {
  return (
    <p
      className="foot"
      data-od-id="data-note"
    >
      <span>Datenbasis: LoaderController.overview</span>
      <span>
        NEU = Daten, die die unified-api heute liefert oder vorbereitet
      </span>
    </p>
  );
}
